import React, { forwardRef } from 'react';
import { AppBar, UserMenu, Logout, useUserMenu, useStore, useGetIdentity } from 'react-admin';
import { MenuItem, ListItemIcon, ListItemText, Box, Divider, Typography } from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import { Link, useLocation } from 'react-router-dom';
import { DashboardResources, pluginResourceProps } from '@src/types';
import type { Partner } from '@src/types';
import { TroveWordmark } from './TroveLogo';

/**
 * Path prefix → header page name. First match wins, so more specific prefixes
 * come first. Grouped pages show their section name (mirrors CustomMenu groups),
 * e.g. /catalog-items renders as "Catalog Management".
 */
const PAGE_NAMES: Array<[string, string]> = [
  ['/select-partner', 'Partner Select'],
  [`/${DashboardResources.CatalogItems}`, 'Catalog Management'],
  [`/${DashboardResources.CatalogFieldsMappingUpload}`, 'Catalog Management'],
  [`/${DashboardResources.CatalogRules}`, 'Catalog Management'],
  [`/${DashboardResources.ShopifyRedirects}`, 'Catalog Management'],
  [`/${DashboardResources.VariantSkus}`, 'Catalog Management'],
  ...pluginResourceProps.map(({ name, options }): [string, string] => [`/${name}`, options?.label ?? name]),
  [`/${DashboardResources.RepairPricing}`, 'Repairs'],
  [`/${DashboardResources.Repairs}`, 'Repairs'],
  [`/${DashboardResources.PricingRules}`, 'Resale Pricing'],
  [`/${DashboardResources.ItemAdjustedPrices}`, 'Resale Pricing'],
  [`/${DashboardResources.ConditionDiscounts}`, 'Resale Pricing'],
  ['/shopify-sync', 'Shopify Sync'],
  ['/devtools/api-docs', 'API Documentation'],
  ['/profile', 'Profile'],
  [`/${DashboardResources.LookerDashboards}`, 'Analytics'],
];

export const getPageName = (pathname: string): string => {
  const match = PAGE_NAMES.find(([prefix]) => pathname.startsWith(prefix));
  if (match) return match[1];
  return pathname === '/' ? 'Welcome' : '';
};

const headerDividerSx = {
  mx: 2,
  my: 'auto',
  height: 20,
  borderColor: 'rgba(255,255,255,0.25)',
};

interface TroveHeaderContentProps {
  partnerName?: string;
  pageName?: string;
  userEmail?: string;
}

/**
 * Presentational header row: wordmark | partner | page ... email. Purely
 * props-driven; the hamburger and user menu are contributed by the
 * surrounding AppBar.
 */
export const TroveHeaderContent = ({ partnerName, pageName, userEmail }: TroveHeaderContentProps) => (
  <>
    {/* Inline SVG (see TroveLogo.tsx) — renders in currentColor, so it picks up
        the app bar text color with no network dependency. */}
    <Box sx={{ ml: 0.5, flexShrink: 0 }}>
      <TroveWordmark height={15} />
    </Box>
    {partnerName && (
      <>
        <Divider orientation="vertical" flexItem sx={headerDividerSx} />
        <Typography variant="body1" sx={{ whiteSpace: 'nowrap', opacity: 0.85 }}>
          {partnerName}
        </Typography>
      </>
    )}
    {pageName && (
      <>
        <Divider orientation="vertical" flexItem sx={headerDividerSx} />
        <Typography variant="body1" sx={{ whiteSpace: 'nowrap', fontWeight: 600 }}>
          {pageName}
        </Typography>
      </>
    )}
    <Box sx={{ flex: 1, minWidth: 8 }} />
    {userEmail && (
      <Typography variant="body2" sx={{ whiteSpace: 'nowrap', opacity: 0.9 }}>
        {userEmail}
      </Typography>
    )}
  </>
);

/** Resolves live header data: selected partner (set by CustomLayout/PartnerSelect),
 * current page from the route, and the logged-in user's email from the auth provider. */
const LiveHeaderContent = () => {
  const location = useLocation();
  const [currentPartner] = useStore<Partner | null>('currentPartner', null);
  const { identity } = useGetIdentity();
  return (
    <TroveHeaderContent
      partnerName={currentPartner?.displayName || currentPartner?.name}
      pageName={getPageName(location.pathname)}
      userEmail={identity?.email}
    />
  );
};

const ProfileMenuItem = forwardRef<HTMLAnchorElement>((props, ref) => {
  const userMenu = useUserMenu();
  return (
    <MenuItem
      ref={ref}
      component={Link}
      to="/profile"
      onClick={userMenu?.onClose}
      {...props}
    >
      <ListItemIcon>
        <PersonIcon fontSize="small" />
      </ListItemIcon>
      <ListItemText>Profile</ListItemText>
    </MenuItem>
  );
});

ProfileMenuItem.displayName = 'ProfileMenuItem';

const CustomUserMenu = () => (
  <UserMenu>
    <ProfileMenuItem />
    <Logout />
  </UserMenu>
);

// react-admin's AppBar contributes the hamburger (SidebarToggleButton) on the left
// and the user menu on the far right; our children render in between.
export const CustomAppBar = () => (
  <AppBar userMenu={<CustomUserMenu />}>
    <LiveHeaderContent />
  </AppBar>
);

export default CustomAppBar;
